import React, { useCallback, useState } from 'react'
import { Button, Tag, Toggle, Loader, Table } from 'rsuite'
import { useFetchSingleMenu } from '@@partner:graphql/query/menu/singleMenu.gql'
import { useUpdateMenuItem } from '@@partner:graphql/mutation/menu/updateMenuItem.gql'
import { GQL_ID } from '@@partner:types:graphql'
import { MenuItem } from '@@partner:types:app'
import { notifyOk, notifyError } from '@@partner:helpers/notification'
import EditIcon from '@rsuite/icons/Edit'
import { generatePath } from 'react-router-dom'
import { ROUTE_SETTINGS_MENUS_EDIT } from '@@partner:config/routes'

const { Column, HeaderCell, Cell } = Table

type MenuEditorProps = {
  menuId: GQL_ID
  menuCode: string
  menuName?: string
  onClose: () => void
}

export default function MenuEditor ({
  menuId,
  menuCode,
  menuName,
  onClose
}: MenuEditorProps) {
  const [ savingItemId, setSavingItemId ] = useState<GQL_ID | null>(null)

  const { data, loading, refetch } = useFetchSingleMenu({ id: menuId })

  const [ updateMenuItem ] = useUpdateMenuItem()

  const handleToggleMenuItem = useCallback(async (item: MenuItem, enabled: boolean) => {
    setSavingItemId(item.id)
    try {
      await updateMenuItem({
        variables: {
          id: item.id,
          input: {
            is_active: enabled
          }
        }
      })
      notifyOk(enabled ? 'Пункт меню включен' : 'Пункт меню отключен')
      refetch()
    }
    catch (error) {
      notifyError('Ошибка изменения статуса пункта меню')
    }
    finally {
      setSavingItemId(null)
    }
  }, [ updateMenuItem, refetch ])

  const menuItems = data?.singleMenu?.menuItems ?? []
  const menu = data?.singleMenu

  const handleOpenFullEditor = useCallback(() => {
    window.open(
      generatePath(ROUTE_SETTINGS_MENUS_EDIT, { menuId }),
      '_blank'
    )
  }, [ menuId ])

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: 16,
        padding: '8px 0',
        borderBottom: '1px solid #e5e5e5'
      }}>
        <h4 style={{ margin: 0 }}>
          Меню {menuName || menu?.name ? `"${menuName || menu?.name}"` : ''}
          {menu?.type && (
            <Tag size="sm" style={{ marginLeft: 8 }}>{menu.type}</Tag>
          )}
        </h4>
        <div>
          <Button 
            appearance="primary" 
            size="sm"
            startIcon={<EditIcon />}
            onClick={handleOpenFullEditor}
          >
            Полный редактор
          </Button>
          <Button 
            appearance="subtle" 
            size="sm" 
            onClick={onClose}
            style={{ marginLeft: 8 }}
          >
            Закрыть
          </Button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Loader size="md" content="Загрузка меню..." />
        </div>
      ) : menuItems.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          Нет пунктов меню
        </div>
      ) : (
        <Table
          data={menuItems}
          autoHeight
          rowHeight={60}
        >
          <Column width={60} align="center">
            <HeaderCell>#</HeaderCell>
            <Cell dataKey="sort_order" />
          </Column>

          <Column width={60} align="center">
            <HeaderCell>Фото</HeaderCell>
            <Cell>
              {(rowData: MenuItem) => (
                rowData.image?.url ? (
                  <img 
                    src={rowData.image.url} 
                    alt={rowData.title}
                    style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                  />
                ) : (
                  <div style={{ 
                    width: 40, 
                    height: 40, 
                    background: '#f5f5f5', 
                    borderRadius: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#ccc',
                    fontSize: 10
                  }}>
                    —
                  </div>
                )
              )}
            </Cell>
          </Column>

          <Column flexGrow={1}>
            <HeaderCell>Название</HeaderCell>
            <Cell>
              {(rowData: MenuItem) => (
                <div>
                  <div style={{ fontWeight: rowData.bold ? 'bold' : 'normal' }}>
                    {rowData.title}
                  </div>
                  {rowData.internal_name && rowData.internal_name !== rowData.title && (
                    <div style={{ fontSize: 11, color: '#999' }}>
                      {rowData.internal_name}
                    </div>
                  )}
                </div>
              )}
            </Cell>
          </Column>

          <Column width={200}>
            <HeaderCell>Ссылка</HeaderCell>
            <Cell>
              {(rowData: MenuItem) => (
                <div style={{ fontSize: 12, color: '#666' }}>
                  <Tag size="sm" style={{ marginRight: 4 }}>{rowData.link_type}</Tag>
                  <span style={{ 
                    maxWidth: 120, 
                    display: 'inline-block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    verticalAlign: 'middle'
                  }}>
                    {rowData.link_url}
                  </span>
                </div>
              )}
            </Cell>
          </Column>

          <Column width={100} align="center">
            <HeaderCell>Статус</HeaderCell>
            <Cell>
              {(rowData: MenuItem) => (
                <Toggle
                  checked={rowData.is_active}
                  loading={savingItemId === rowData.id}
                  size="sm"
                  onChange={(checked) => handleToggleMenuItem(rowData, checked)}
                />
              )}
            </Cell>
          </Column>
        </Table>
      )}

      <div style={{ marginTop: 16, padding: 12, background: '#f9f9f9', borderRadius: 6 }}>
        <p style={{ margin: 0, fontSize: 12, color: '#666' }}>
          💡 Для добавления новых пунктов меню или редактирования деталей, используйте <a onClick={handleOpenFullEditor} style={{ cursor: 'pointer', color: '#1675e0' }}>полный редактор меню</a>.
        </p>
      </div>
    </div>
  )
}
